<template>
  <template v-if="socialProviders.length > 0">
    <div class="flex items-center justify-center space-x-4 w-full">
      <ASocialProviderButton
        v-for="provider in socialProviders"
        :key="provider"
        :provider
        v-bind="socialProviderButtonProps"
      />
    </div>

    <UDivider label="ou" class="mt-4 mb-3" />
  </template>

  <AForm
    id="loginForm"
    v-bind="attrs"
    :close-modal-after-submit="false"
    :submit-label="$t('components.aLoginForm.submitLoginLabel')"
    :localized="false"
    :schema
    :state
    :disable-swal-for-statuses="[401]"
    :handler="signInHandler"
    @success="isLoggedIn"
    @errored="
      (_data, _err, lastError) => {
        emit('error', state.username ?? '', lastError);
      }
    "
  >
    <UFormGroup size="lg" :label="$t('form.email.label')" name="username">
      <UInput
        v-model="state.username"
        type="email"
        autocomplete="email"
        placeholder="email@example.com"
        trailing-icon="fluent:mail-32-regular"
      />
    </UFormGroup>

    <UFormGroup size="lg" :label="$t('form.password.label')" name="password">
      <UInput
        v-model="state.password"
        type="password"
        autocomplete="password"
        placeholder="********************"
        trailing-icon="fluent:lock-closed-key-16-regular"
      />
      <template #hint>
        <UButton
          color="white"
          variant="link"
          :padded="false"
          class="justify-center"
          size="sm"
          @click="emit('password-forgotten')"
        >
          {{ $t("components.aLoginForm.submitRetrievePasswordLabel") }}
        </UButton>
      </template>
    </UFormGroup>
  </AForm>
</template>

<script lang="ts" setup>
import type { ALoginFormProps, LastError } from '#common/types'

import { z } from 'zod'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<ALoginFormProps>(), {
  signInAttrs: () => ({}),
  socialProviderButtonProps: () => ({}),
  socialProviders: () => [],
})
const emit = defineEmits<{
  'error': [email: string, lastError: Ref<LastError>]
  'logged-in': []
  'password-forgotten': []
}>()
const attrs = useAttrs()
const { t } = useI18n()
const route = useRoute()
const { $me } = useNuxtApp()
const { auth } = useAppConfig()
const { refresh } = useAuth()
const { setToken } = useAuthState()
const { signInAttrs } = toRefs(props)

if (route.query.redirect) {
  signInAttrs.value.callbackUrl = route.query.redirect as string
}

const schema = z.object({
  password: z.string().min(1, { message: t('validation.required') }),
  username: z.string().email(t('validation.email')),
})

const state = ref<{
  password?: string
  username?: string
}>({})

async function isLoggedIn(data: any, res: any) {
  setToken(res.access_token)
  await refresh()
  emit('logged-in')
  await navigateTo(auth.redirectToAfterLogin($me.value), { external: true })
}

async function signInHandler({ data }: any) {
  return await $fetch('/api/auth/token', {
    body: data,
    method: 'POST',
  })
}
</script>
